import React, { useState, useRef, useEffect } from 'react';
import './styles.css';

interface AIInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (question: string, cellContext?: CellContext) => void;
  cellContext?: CellContext;
}

interface CellContext {
  row: number;
  col: number;
  cellValue: any;
  columnName: string;
  fieldName: string;
  rowId: string;
}

const AIInputModal: React.FC<AIInputModalProps> = ({
  isOpen,
  onClose,
  onSend,
  cellContext,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // 预设问题模板
  const questionTemplates = [
    '这个成分的作用是什么？',
    '这个成分有什么副作用？',
    '推荐这个成分的合适用量',
    '这个成分与其他成分的兼容性如何？',
  ];

  // 打开时自动聚焦
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
      
      // 根据单元格上下文生成智能提示
      if (cellContext) {
        const smartQuestion = generateSmartQuestion(cellContext);
        setInputValue(smartQuestion);
      }
    }
  }, [isOpen, cellContext]);

  // 点击外部关闭
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // 根据单元格上下文生成智能问题
  const generateSmartQuestion = (context: CellContext): string => {
    const { columnName, cellValue, fieldName } = context;
    
    if (fieldName === 'tradeName') {
      return `请分析"${cellValue}"这个成分的特性和使用建议`;
    } else if (fieldName === 'function') {
      return `关于"${Array.isArray(cellValue) ? cellValue.join(', ') : cellValue}"功能的成分，请提供更多信息`;
    } else if (fieldName === 'percentage') {
      return `这个成分${cellValue}%的用量是否合适？请给出建议`;
    } else {
      return `关于${columnName}列中的"${cellValue}"，我想了解更多信息`;
    }
  };

  // 处理发送
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;

    setIsLoading(true);
    
    try {
      await onSend(inputValue.trim(), cellContext);
      setInputValue('');
      onClose();
    } catch (error) {
      console.error('发送问题失败:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // 处理模板点击
  const handleTemplateClick = (template: string) => {
    setInputValue(template);
    textareaRef.current?.focus();
  };

  if (!isOpen) return null;

  return (
    <div className="ai-input-modal-overlay">
      <div ref={modalRef} className="ai-input-modal">
        {/* 头部 */}
        <div className="ai-input-modal-header">
          <div className="ai-input-modal-title">
            <span>AI助手</span>
            {cellContext && (
              <span className="ai-input-modal-context">
                ({cellContext.columnName}: {String(cellContext.cellValue)})
              </span>
            )}
          </div>
          <button 
            className="ai-input-modal-close"
            onClick={onClose}
            aria-label="关闭"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        {/* 内容区 */}
        <div className="ai-input-modal-content">
          {/* 快速模板 */}
          {/* <div className="ai-input-modal-templates">
            <div className="ai-input-modal-templates-title">快速提问</div>
            <div className="ai-input-modal-templates-list">
              {questionTemplates.map((template, index) => (
                <button
                  key={index}
                  className="ai-input-modal-template-btn"
                  onClick={() => handleTemplateClick(template)}
                >
                  {template}
                </button>
              ))}
            </div>
          </div> */}

          {/* 输入区 */}
          <div className="ai-input-modal-input-area">
            <textarea
              ref={textareaRef}
              className="ai-input-modal-textarea"
              placeholder="请输入您的问题..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={4}
              maxLength={500}
            />
            <div className="ai-input-modal-input-footer">
              <span className="ai-input-modal-char-count">
                {inputValue.length}/500
              </span>
              <div className="ai-input-modal-hint">
                按 Ctrl+Enter 发送
              </div>
            </div>
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="ai-input-modal-footer">
          <button 
            className="ai-input-modal-btn ai-input-modal-btn-secondary"
            onClick={onClose}
          >
            取消
          </button>
          <button 
            className="ai-input-modal-btn ai-input-modal-btn-primary"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
          >
            {isLoading ? (
              <>
                <div className="ai-input-modal-loading-spinner"></div>
                发送中...
              </>
            ) : (
              <>
                <svg className="ai-input-modal-send-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                </svg>
                发送问题
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIInputModal;